import { hasPermission } from "../../../app/auth.js";
import { applySheets } from "../../../app/utils/styleCache.js";
import { DateFilterManager } from "./utils/dateFilterManager.js";

export class AppFilter extends HTMLElement {
    static get observedAttributes() { return ["selected"]; }

    constructor() {
        super();
        this.attachShadow({ mode: "open" });
        this.isMounted = false;
    }

    connectedCallback() {
        this.isInitializing = true;

        this.shadowRoot.innerHTML = `
            <style>
                :host { opacity: 0; display: block; }
                :host([loaded]) { opacity: 1; transition: opacity 0.25s ease-in; }
            </style>
            <div class="filterBar">
                <div class="filter" id="scopeFilter">
                    <div class="slider" id="filterSlider"></div>
                    <button class="filterBtn" data-value="user"><i class="fa-solid fa-user"></i><span>Meine Einträge</span></button>
                    <button class="filterBtn" data-value="team"><i class="fa-solid fa-users"></i><span>Mein Team</span></button>
                    <button class="filterBtn" data-value="all"><i class="fa-solid fa-building"></i><span>Alle Einträge</span></button>
                </div>
                <div class="dateFilterHost">
                    <div id="periodModeHost"></div>
                    <div class="filterSep"></div>
                    <div id="dateNavHost"></div>
                </div>
            </div>
        `;

        this.isMounted = true;

        const periodModeHost = this.shadowRoot.getElementById("periodModeHost");
        const dateNavHost = this.shadowRoot.getElementById("dateNavHost");
        this.dateManager = new DateFilterManager(periodModeHost, dateNavHost, (detail) => { this.dispatchEvent(new CustomEvent('date-filter-change', {detail, bubbles: true, composed: true}))});
        this.dateManager.setInitializing(true);
        this.dateManager.init();

        const root = this.shadowRoot;
        this.buttons = root.querySelectorAll("#scopeFilter .filterBtn");

        this.buttons.forEach(btn => {btn.addEventListener("click", () => this.select(btn.dataset.value))});

        this.applyPermissions();
        const firstVisible = [...this.buttons].find(btn => !btn.hidden);

        this.isInitializing = false;
        this.dateManager.setInitializing(false);
        const savedValue = this.getAttribute("selected");
        const savedBtn = savedValue ? [...this.buttons].find(b => b.dataset.value === savedValue) : null;
        const effectiveValue = (savedBtn && !savedBtn.hidden) ? savedValue : (firstVisible?.dataset.value ?? "user");
        this.select(effectiveValue, { silent: true, animate: false });

        this.ready = applySheets(this.shadowRoot, [
                "components/shared/app-input/app-input.css",
                "components/shared/app-filter/app-filter.css",
            ])
            .then(() => document.fonts.ready)
            .then(() => new Promise(resolve => requestAnimationFrame(() => {
                this.moveSlider(false);
                this.dateManager?.refreshLayout();
                this.setAttribute("loaded", "");
                resolve();
            })));
    }

    disconnectedCallback() {
        this.dateManager?.destroy();
    }

    attributeChangedCallback(name, oldVal, newVal) {
        if (!this.isMounted || oldVal === newVal) return;
        if (name === "selected") this.select(newVal, { silent: true });
    }

    select(value, { silent = false, animate = true } = {}) {
        const changed = this._value !== value;
        this.buttons.forEach(btn => {
            const isActive = btn.dataset.value === value;
            btn.classList.toggle("active", isActive);
            btn.setAttribute("aria-selected", String(isActive));
        });
        this._value = value;
        this.moveSlider(animate);
        if (!silent && changed) {
            this.dispatchEvent(new CustomEvent("filter-change", {
                detail: { value },
                bubbles: true,
                composed: true
            }));
        }
    }

    moveSlider(animate) {
        const slider = this.shadowRoot.getElementById('filterSlider');
        const activeBtn = [...this.buttons].find(btn => btn.classList.contains('active') && !btn.hidden);
        if (!slider || !activeBtn || !activeBtn.offsetWidth) return;
        if (animate) {
            slider.style.transition = 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), width 0.25s cubic-bezier(0.4, 0, 0.2, 1)';
            void slider.offsetWidth;
        } else {
            slider.style.transition = 'none';
        }
        slider.style.width = `${activeBtn.offsetWidth}px`;
        slider.style.transform = `translateX(${activeBtn.offsetLeft}px)`;
    }

    applyPermissions() {
        this.buttons.forEach(btn => {
            const perm = this.getAttribute(`perm-${btn.dataset.value}`);
            if (perm) btn.hidden = !hasPermission(perm);
        });

        const btn = (val) => [...this.buttons].find(b => b.dataset.value === val);
        const teamVisible = !btn("team")?.hidden;
        const allVisible  = !btn("all")?.hidden;

        if ((teamVisible || allVisible) && btn("user")) btn("user").hidden = false;
        if (allVisible && btn("team")) btn("team").hidden = false;

        const onlyOneVisible = [...this.buttons].filter(btn => !btn.hidden).length <= 1;
        this.shadowRoot.querySelector('#scopeFilter').style.display = onlyOneVisible ? 'none' : '';
    }
 
    setDateFilter({ period, year, month, from, to } = {}) {
        this.dateManager?.restoreFromUrl({ period, year, month, from, to });
    }

    get value() { return this._value; }
    set value(v) { this.select(v); }
}

customElements.define("app-filter", AppFilter);
